import EventBus from 'eventing-bus';
import Box from '@material-ui/core/Box';
import TextField from '@mui/material/TextField';
import React, { useState, useEffect } from 'react';
import { CircularProgress } from "@material-ui/core";
import Typography from '@material-ui/core/Typography';
import { useSelector, useDispatch } from 'react-redux';
import { Checkbox, ListItemText } from '@mui/material';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import { MenuItem, FormControl, Select, } from '@mui/material';
import { ValidatorForm, TextValidator } from 'react-material-ui-form-validator';

import { getApiKeys, createApiKey, deleteApiKey, setApiKeyCreated } from '../../store/Settings';
import TableAnimationFiles from '../Animations/TableAnimationFiles.js';
import notFoundImg from '../../static/img/not-found.png';

const optionsStatus = [
    // Global permissions
    { value: 'all:all', label: 'all:all', category: 'Global', description: 'Full access to all resources' },
    { value: 'all:admin', label: 'all:admin', category: 'Global', description: 'Admin access to all resources' },

    // Users resource
    { value: 'users:read', label: 'users:read', category: 'Users', description: 'Read user profiles and data' },
    { value: 'users:create', label: 'users:create', category: 'Users', description: 'Create new users' },
    { value: 'users:update', label: 'users:update', category: 'Users', description: 'Update user profiles' },
    { value: 'users:delete', label: 'users:delete', category: 'Users', description: 'Delete users' },
    { value: 'users:all', label: 'users:all', category: 'Users', description: 'Full access to users' },

    // Campaigns resource
    { value: 'campaigns:read', label: 'campaigns:read', category: 'Campaigns', description: 'Read campaign data' },
    { value: 'campaigns:create', label: 'campaigns:create', category: 'Campaigns', description: 'Create new campaigns' },
    { value: 'campaigns:update', label: 'campaigns:update', category: 'Campaigns', description: 'Update campaigns' },
    { value: 'campaigns:delete', label: 'campaigns:delete', category: 'Campaigns', description: 'Delete campaigns' },
    { value: 'campaigns:all', label: 'campaigns:all', category: 'Campaigns', description: 'Full access to campaigns' },

    // Contacts resource
    { value: 'contacts:read', label: 'contacts:read', category: 'Contacts', description: 'Read contact data' },
    { value: 'contacts:create', label: 'contacts:create', category: 'Contacts', description: 'Create new contacts' },
    { value: 'contacts:update', label: 'contacts:update', category: 'Contacts', description: 'Update contact information' },
    { value: 'contacts:delete', label: 'contacts:delete', category: 'Contacts', description: 'Delete contacts' },
    { value: 'contacts:all', label: 'contacts:all', category: 'Contacts', description: 'Full access to contacts' },

    // Agents resource
    { value: 'agents:read', label: 'agents:read', category: 'Agents', description: 'Read AI agent data' },
    { value: 'agents:create', label: 'agents:create', category: 'Agents', description: 'Create new agents' },
    { value: 'agents:update', label: 'agents:update', category: 'Agents', description: 'Update agent configuration' },
    { value: 'agents:delete', label: 'agents:delete', category: 'Agents', description: 'Delete agents' },
    { value: 'agents:all', label: 'agents:all', category: 'Agents', description: 'Full access to agents' },

    // Phones resource
    { value: 'phones:read', label: 'phones:read', category: 'Phones', description: 'Read phone numbers and call logs' },
    { value: 'phones:create', label: 'phones:create', category: 'Phones', description: 'Create phone numbers' },
    { value: 'phones:update', label: 'phones:update', category: 'Phones', description: 'Update phone settings' },
    { value: 'phones:delete', label: 'phones:delete', category: 'Phones', description: 'Delete phone numbers' },
    { value: 'phones:all', label: 'phones:all', category: 'Phones', description: 'Full access to phones' },

    // Integrations resource
    { value: 'integrations:read', label: 'integrations:read', category: 'Integrations', description: 'Read integration data' },
    { value: 'integrations:create', label: 'integrations:create', category: 'Integrations', description: 'Create new integrations' },
    { value: 'integrations:update', label: 'integrations:update', category: 'Integrations', description: 'Update integrations' },
    { value: 'integrations:delete', label: 'integrations:delete', category: 'Integrations', description: 'Delete integrations' },
    { value: 'integrations:all', label: 'integrations:all', category: 'Integrations', description: 'Full access to integrations' },

    // HubSpot resource
    { value: 'hubspot:read', label: 'hubspot:read', category: 'HubSpot', description: 'Read HubSpot data' },
    { value: 'hubspot:create', label: 'hubspot:create', category: 'HubSpot', description: 'Create HubSpot records' },
    { value: 'hubspot:update', label: 'hubspot:update', category: 'HubSpot', description: 'Update HubSpot data' },
    { value: 'hubspot:delete', label: 'hubspot:delete', category: 'HubSpot', description: 'Delete HubSpot records' },
    { value: 'hubspot:all', label: 'hubspot:all', category: 'HubSpot', description: 'Full access to HubSpot' },
];

const ApiKeysTab = ({ tabOptionsPhone }) => {
    const dispatch = useDispatch();
    const { apiKeys, apiKeyCreated, currentWorkspace, isLoading } = useSelector(state => state['Settings']);

    const [loading, setLoading] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [showCreateAPIKey, setShowCreateAPIKey] = useState(false);
    const [openStatusDetails, setOpenStatusDetails] = useState(false);
    const [searchStatusDetails, setSearchStatusDetails] = useState('');
    const [formData, setFormData] = useState({ name: '', scopes: [] });
    const [selectedStatusDetails, setSelectedStatusDetails] = useState([]);

    useEffect(() => { dispatch(getApiKeys()); }, []);

    const handleSelectStatuesDetails = (selectedValues) => {
        setSearchStatusDetails('');
        setOpenStatusDetails(false);
        setSelectedStatusDetails(selectedValues);
        setFormData({ ...formData, scopes: selectedValues });
    };

    const filteredoptionsStatusDetails = optionsStatus.filter((status) => status.label.toLowerCase().includes(searchStatusDetails.toLowerCase()));

    const handleCreateApiKey = (e) => {
        e.preventDefault();
        if (!formData.name || formData.scopes.length === 0) return EventBus.publish('error', 'Please fill in all fields');

        setLoading(true);
        dispatch(createApiKey({ ...formData, workspaceId: currentWorkspace['_id'] }));
        setFormData({ name: '', scopes: [] });

        // Reset loading state after a timeout as fallback
        setTimeout(() => {
            setLoading(false);
            setShowCreateAPIKey(false);
            setSelectedStatusDetails([]);
        }, 1000);
    };

    const handleDeleteApiKey = (apiKeyId) => dispatch(deleteApiKey(apiKeyId));

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    return (

        <div className='content account-tabs'>
            <div className='tabs-content'>
                <div className='title-top-block'>
                    <div className='left-area'>
                        <h2>API Keys</h2>
                    </div>
                    <div className='right-btns'>
                        {/* <button className='btn-style-border'><i className='fa fa-download'></i> Download Invoice</button> */}
                        <button className='btn-style-new' onClick={() => setShowCreateAPIKey(!showCreateAPIKey)}>Create API Key</button>
                    </div>
                </div>
                <div className='billing-right-content'>
                    <div className='bottom-content-area'>
                        <div className='api-keys-tab'>
                            <div className='api-keys-tab-box'>
                                {showCreateAPIKey ? (
                                    <div className='api-keys-tab-box-content style-two'>
                                        <h4>Create API Key</h4>
                                        <ValidatorForm className='validator-form' onSubmit={handleCreateApiKey}>
                                            <div className='group-form'>
                                                <label className='form-label'>Name</label>
                                                <TextValidator
                                                    required
                                                    type='text'
                                                    name='name'
                                                    margin='dense'
                                                    variant='outlined'
                                                    value={formData['name']}
                                                    placeholder='Enter name'
                                                    validators={['required']}
                                                    className='login-input-fields'
                                                    errorMessages={['Name can not be empty']}
                                                    onChange={(e) => setFormData({ ...formData, name: e['target']['value'] })}
                                                />
                                            </div>
                                            <div className='group-form'>
                                                <label className='form-label'>Scopes</label>
                                                <FormControl fullWidth>
                                                    <Select
                                                        multiple
                                                        displayEmpty
                                                        open={openStatusDetails}
                                                        value={selectedStatusDetails}
                                                        onOpen={() => setOpenStatusDetails(true)}
                                                        onClose={() => setOpenStatusDetails(false)}
                                                        onChange={(e) => handleSelectStatuesDetails(e['target']['value'])}
                                                        renderValue={(selected) => {
                                                            if (!selected || selected.length === 0) return 'Select Scopes';
                                                            return (
                                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                    {selected.map((value) => {
                                                                        const selectedOption = optionsStatus.find((option) => option['label'] === value);
                                                                        return selectedOption ? (
                                                                            <Box key={value} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                                <i style={{ marginRight: '6px' }}>{selectedOption['icon']}</i>
                                                                                <Typography sx={{ fontSize: '14px' }}>{selectedOption['label']}</Typography>
                                                                            </Box>
                                                                        ) : null;
                                                                    })}
                                                                </Box>
                                                            );
                                                        }}
                                                        MenuProps={{ disableAutoFocusItem: true }}
                                                    >
                                                        {/* Search Field - Keeps Dropdown Open on Click */}
                                                        <MenuItem disableRipple>
                                                            <TextField
                                                                fullWidth
                                                                autoFocus
                                                                variant='standard'
                                                                value={searchStatusDetails}
                                                                placeholder='Search Scopes...'
                                                                onChange={(e) => setSearchStatusDetails(e['target']['value'])}
                                                                onClick={(e) => e.stopPropagation()}
                                                            />
                                                        </MenuItem>

                                                        {/* Show Filtered Options */}
                                                        {filteredoptionsStatusDetails.length > 0 ?
                                                            filteredoptionsStatusDetails.map((status) =>
                                                                <MenuItem key={status['label']} value={status['label']} title={status['description']}>
                                                                    <Checkbox checked={selectedStatusDetails.indexOf(status['label']) > -1} style={{ marginRight: '8px' }} />
                                                                    <ListItemText
                                                                        primary={
                                                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                                                <span style={{ fontWeight: '500' }}>{status['label']}</span>
                                                                                <span style={{ fontSize: '10px', color: '#4b5563', }}>
                                                                                    {status['description']}
                                                                                </span>
                                                                            </div>
                                                                        }
                                                                    />
                                                                </MenuItem>
                                                            )
                                                            : <MenuItem disabled>No results found</MenuItem>}
                                                    </Select>
                                                </FormControl>
                                            </div>
                                            <div className='group-form'> <p>For security reasons, the scopes cannot be changed after the API key is created.</p> </div>
                                            <div className='group-form btn-group'>
                                                <button className='btn-style-border' onClick={() => setShowCreateAPIKey(false)} disabled={loading}>Cancel</button>
                                                <button className='btn-style-new' type='submit' disabled={loading}>
                                                    {loading ? 'Creating...' : 'Create'}
                                                </button>
                                            </div>
                                        </ValidatorForm>
                                    </div>
                                ) : (
                                    <>
                                        {isLoading ? (
                                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                                                <TableAnimationFiles />
                                            </div>
                                        ) : apiKeys.length === 0 ? (
                                            <div className='api-keys-tab-box-content api-key-notfound'>
                                                <div className='img-box'>
                                                    <img alt="..." src={notFoundImg} />
                                                </div>
                                                <p>No API keys found.</p>
                                            </div>
                                        ) : (
                                            <div className='existing-api-keys'>
                                                {apiKeys.map((apiKey) => (
                                                    <div key={apiKey['_id']} className='api-keys-created'>
                                                        <div className='left-content'>
                                                            <h4>{apiKey['name']}</h4>
                                                            <div className='date-area'>
                                                                <p>{formatDate(apiKey['createdAt'])}</p>
                                                                <div className='scope-area'>
                                                                    {apiKey['permissions'] && Object.entries(apiKey['permissions']).map(([resource, action]) =>
                                                                        <span key={`${resource}:${action}`} className='select-scope'>{`${resource}:${action}`}</span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className='right-content'>
                                                            <button title='Delete API key' className={`btn-removed`} onClick={() => handleDeleteApiKey(apiKey['_id'])}>
                                                                <i className='icon'><svg class="MuiSvgIcon-root MuiSvgIcon-fontSizeMedium css-1umw9bq-MuiSvgIcon-root" focusable="false" aria-hidden="true" viewBox="0 0 24 24" data-testid="DeleteIcon"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6zM19 4h-3.5l-1-1h-5l-1 1H5v2h14z"></path></svg></i>
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* --------------- Add New Agent--------------- */}
            <Modal isOpen={apiKeyCreated} toggle={() => dispatch(setApiKeyCreated(null))} className={`main-modal new-user-modal modal-dialog-centered`}>
                <ModalHeader toggle={() => dispatch(setApiKeyCreated(null))}></ModalHeader>
                <ModalBody>
                    <div className='add-content'>
                        <h2>API Key Created Successfully</h2>
                        <div className='templates-inner'>
                            <p className='mt-3'>Here is your new API key. Please copy it and store it securely. For security reasons, you will not be able to see it again.</p>
                            <div className='api-key-box-copy'>
                                <h4>{apiKeyCreated?.['key'] || 'API Key'}
                                    <CopyToClipboard text={apiKeyCreated?.['key'] || ''} onCopy={() => EventBus.publish('success', 'API KEY Copied!')}>
                                        <span className='copy-btn fa fa-copy'></span>
                                    </CopyToClipboard>
                                </h4>
                            </div>
                            <div className='btn-group'>
                                <button className='btn-style-new' onClick={() => dispatch(setApiKeyCreated(null))}>Ok</button>
                            </div>
                        </div>
                    </div>
                </ModalBody>
            </Modal>
        </div>
    );
};

export default ApiKeysTab;